import React, { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import getRequiredUserData from "../../utils/getBranchInfo";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { useTheme } from "@mui/material/styles";
import { getOrderTypes } from "../../api/services/orderTypes/orderTypes";

// ---- Timeline options ----
const timelineOptions = [
  { label: "Today", value: "today" },
  { label: "This Week", value: "thisweek" },
  { label: "15 Days", value: "15days" },
  { label: "1 Month", value: "1month" },
  { label: "6 Months", value: "6month" },
  { label: "1 Year", value: "1year" },
  { label: "Max", value: "max" },
];

function getStartDate(timeline) {
  const now = new Date();
  switch (timeline) {
    case "today": {
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      return start;
    }
    case "thisweek": {
      const start = new Date(now);
      const day = start.getDay(); // 0 (Sun) - 6 (Sat)
      const diff = start.getDate() - day + (day === 0 ? -6 : 1); // Monday as first day
      start.setDate(diff);
      start.setHours(0, 0, 0, 0);
      return start;
    }
    case "15days": {
      const start = new Date(now);
      start.setDate(start.getDate() - 15);
      start.setHours(0, 0, 0, 0);
      return start;
    }
    case "1month":
      return new Date(now.setMonth(now.getMonth() - 1));
    case "6month":
      return new Date(now.setMonth(now.getMonth() - 6));
    case "1year":
      return new Date(now.setFullYear(now.getFullYear() - 1));
    case "max":
      return null;
    default:
      return new Date(now.setMonth(now.getMonth() - 1));
  }
}

const OrderCounts = () => {
  const params = useParams();
  let branchId = params.id;
  if (!branchId) {
    const userData = getRequiredUserData();
    branchId = userData?.requiredId;
  }

  const theme = useTheme();
  const db = getFirestore();

  const [categories, setCategories] = useState([]);
  const [orderTypesMap, setOrderTypesMap] = useState({});
  const [typeCounts, setTypeCounts] = useState({});
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("today");

  // --- Fetch order type definitions ---
  useEffect(() => {
    getOrderTypes().then((cats) => {
      const customCategory = {
        name: "custom",
        orderTypes: [{ name: "Custom Orders", measurement: "" }],
      };
      const allCategories = [...cats, customCategory];
      setCategories(allCategories);

      const map = {};
      allCategories.forEach((cat) => {
        map[cat.name] = cat.orderTypes;
      });
      setOrderTypesMap(map);
    });
  }, []);

  // ---- Fetch and aggregate orders ----
  useEffect(() => {
    if (!branchId) return;

    const fetchCounts = async () => {
      try {
        // 1. Fetch all orders for this branch
        const q = query(
          collection(db, "allTimeOrders"),
          where("branchId", "==", branchId)
        );
        const snap = await getDocs(q);
        const allOrders = snap.docs.map((doc) => doc.data());
        // console.log(
        //   `Fetched ${allOrders.length} orders for branch ${branchId}`
        // );

        // 2. Prepare aggregation map
        const counts = {};

        for (const order of allOrders) {
          const { categoryId, type, date, quantity = 0 } = order;

          const orderDate = date?.seconds
            ? new Date(date.seconds * 1000)
            : new Date(date);

          // Iterate through timelines
          for (const { value: timeline } of timelineOptions) {
            const startDate = getStartDate(timeline);
            if (startDate && orderDate < startDate) continue;

            const catKey = categoryId || "unknown";
            const typeKey = type || "Custom Orders";

            if (!counts[catKey]) counts[catKey] = {};
            if (!counts[catKey][typeKey]) {
              counts[catKey][typeKey] = {};
              timelineOptions.forEach(
                (opt) =>
                  (counts[catKey][typeKey][opt.value] = {
                    count: 0,
                    quantity: 0,
                  })
              );
            }

            counts[catKey][typeKey][timeline].count += 1;
            counts[catKey][typeKey][timeline].quantity += parseInt(quantity);
          }
        }

        // 3. Transform to UI structure
        const finalCounts = {};
        for (const cat of categories) {
          const catRows = [];
          const catData = counts[cat.name] || {};

          // include known types
          const types = orderTypesMap[cat.name] || [];
          for (const type of types) {
            const tName = type.name;
            const rowCounts = catData[tName] || {};
            const filledCounts = {};
            timelineOptions.forEach((opt) => {
              filledCounts[opt.value] = rowCounts[opt.value] || {
                count: 0,
                quantity: 0,
              };
            });
            catRows.push({
              type: tName,
              measurement: type.measurement || "",
              counts: filledCounts,
            });
          }

          // include any extra types found in data
          Object.keys(catData).forEach((tName) => {
            if (!types.find((t) => t.name === tName)) {
              catRows.push({
                type: tName,
                measurement: "",
                counts: catData[tName],
              });
            }
          });

          finalCounts[cat.name] = catRows;
        }

        setTypeCounts(finalCounts);
      } catch (err) {
        console.error("Error fetching counts:", err);
      }
    };

    fetchCounts();
  }, [branchId, categories, orderTypesMap]);

  // ---- Sorting ----
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedTypeCounts = useMemo(() => {
    const sorted = {};
    Object.keys(typeCounts).forEach((category) => {
      sorted[category] = [...(typeCounts[category] || [])].sort((a, b) => {
        if (orderBy === "type") {
          return order === "asc"
            ? a.type.localeCompare(b.type)
            : b.type.localeCompare(a.type);
        } else {
          return order === "asc"
            ? (a.counts[orderBy]?.count || 0) - (b.counts[orderBy]?.count || 0)
            : (b.counts[orderBy]?.count || 0) - (a.counts[orderBy]?.count || 0);
        }
      });
    });
    return sorted;
  }, [typeCounts, order, orderBy]);

  // ---- UI ----
  return (
    <Box
      m="1.5rem 2.5rem"
      sx={{
        backgroundColor: theme.palette.background.default,
        height: "100%",
        position: "relative",
      }}
    >
      <Typography variant="h4" sx={{ mb: 2 }}>
        Order Counts
      </Typography>

      {categories.map((category) => (
        <Box key={category.name} sx={{ mb: 4 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {category.name}
          </Typography>
          <TableContainer
            component={Paper}
            sx={{
              backgroundColor: theme.palette.background.alt,
              color: theme.palette.secondary[200],
              border: `1px solid ${
                theme.palette.mode === "dark" ? "#323E8B" : "#C5C7D7"
              }`,
              borderRadius: ".1rem",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{ backgroundColor: theme.palette.background.alt }}
                >
                  <TableCell
                    sx={{
                      color: theme.palette.secondary[50],
                      borderBottom: `1px solid ${
                        theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                      }`,
                    }}
                  >
                    <TableSortLabel
                      active={orderBy === "type"}
                      direction={orderBy === "type" ? order : "asc"}
                      onClick={() => handleRequestSort("type")}
                    >
                      Item
                    </TableSortLabel>
                  </TableCell>

                  {timelineOptions.map((timeline) => (
                    <TableCell
                      key={timeline.value}
                      sx={{
                        color: theme.palette.secondary[50],
                        borderBottom: `1px solid ${
                          theme.palette.mode === "dark" ? "#323E8B" : "#E4E6F2"
                        }`,
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === timeline.value}
                        direction={orderBy === timeline.value ? order : "desc"}
                        onClick={() => handleRequestSort(timeline.value)}
                      >
                        {timeline.label}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {(sortedTypeCounts[category.name] || []).map((row) => (
                  <TableRow key={row.type}>
                    <TableCell sx={{ color: theme.palette.secondary[50] }}>
                      {row.type} (count){" "}
                      {row.measurement && `(${row.measurement})`}
                    </TableCell>

                    {timelineOptions.map((timeline) => (
                      <TableCell
                        key={timeline.value}
                        sx={{ color: theme.palette.secondary[50] }}
                      >
                        {row.counts[timeline.value]?.count || 0}
                        {row.type !== "Custom Orders" &&
                          ` (${row.counts[timeline.value]?.quantity || 0})`}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}

                {/* Totals */}
                <TableRow>
                  <TableCell
                    sx={{
                      color: theme.palette.secondary[50],
                      fontWeight: "bold",
                    }}
                  >
                    Total (count)
                  </TableCell>
                  {timelineOptions.map((timeline) => (
                    <TableCell
                      key={timeline.value}
                      sx={{
                        color: theme.palette.secondary[50],
                        fontWeight: "bold",
                      }}
                    >
                      {(sortedTypeCounts[category.name] || []).reduce(
                        (acc, row) =>
                          acc + (row.counts[timeline.value]?.count || 0),
                        0
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      ))}
    </Box>
  );
};

export default OrderCounts;
